import type { Module } from "vuex/types/index.d.ts";
import { $api } from "@/services/api";
import store from "@/store";
import useFormatter from "@/use/formatter";
import useFormHelpers from "@/use/formHelpers";
import { CitizenshipStatusTypeIds, ProgramIds } from "@/config/constants";
import type { Option } from "@/models/form";

const { formatDateOfBirth } = useFormatter();
const { compareTwoDates } = useFormHelpers();

interface IApplicantPersonalInfoState {
  currentPersonalInfo: Record<string, any>;
  sameDOB: undefined | boolean;
  sameSSN: undefined | boolean;
  meetsAOM: undefined | boolean;
  applicantState: undefined | string | number;
  applicantAge: undefined | number;
  applicantDateOfBirth: undefined | string | Date;
}

const applicantPersonalInformationModule: Module<IApplicantPersonalInfoState, any> = {
  namespaced: true,
  state: {
    currentPersonalInfo: {} as any,
    sameDOB: undefined as undefined | boolean,
    sameSSN: undefined as undefined | boolean,
    meetsAOM: undefined as undefined | boolean,
    applicantState: undefined as any,
    applicantAge: undefined as undefined | number,
    applicantDateOfBirth: undefined as undefined | Date | string,
  },
  mutations: {
    SET_APPLICANT_STATE(state: IApplicantPersonalInfoState, applicantState: any) {
      state.applicantState = applicantState;
    },
    SET_CURRENT_PERSONAL_INFO(state: IApplicantPersonalInfoState, personalInfo: any) {
      state.currentPersonalInfo = personalInfo;
    },
    SET_SAME_DOB(state: IApplicantPersonalInfoState, flag: boolean) {
      state.sameDOB = flag;
    },
    SET_SAME_SSN(state: IApplicantPersonalInfoState, flag: boolean) {
      state.sameSSN = flag;
    },
    SET_APPLICANT_AGE(state: IApplicantPersonalInfoState, age: number | undefined) {
      state.applicantAge = age;
    },
    SET_APPLICANT_DATE_OF_BIRTH(state: IApplicantPersonalInfoState, dob: string) {
      state.applicantDateOfBirth = dob;
    },
    RESET_ALL_STATES(state: IApplicantPersonalInfoState) {
      state.currentPersonalInfo = {};
      state.sameDOB = undefined;
      state.sameSSN = undefined;
      state.meetsAOM = undefined;
      state.applicantState = undefined;
      state.applicantAge = undefined;
      state.applicantDateOfBirth = undefined;
    },
  },
  actions: {
    async compareDateOfBirths({ state, commit }) {
      if (!state.applicantDateOfBirth) {
        commit("SET_SAME_DOB", false);
        return;
      }

      const dob1 = new Date(formatDateOfBirth(store?.state?.appData?.applicants[0]?.dateOfBirth));
      const dob2 = new Date(state.applicantDateOfBirth);

      const compare = compareTwoDates(dob1, dob2);
      commit("SET_SAME_DOB", compare);
    },
    async getCitizenshipStatusOptions() {
      const applicationId = store.state.appData.application.id;
      const productTypeId = store.state.appData.application.productTypeId;
      const lenderId = store.state.appData.application.lenderId;
      const programTypeId = store.state.appData.application.programTypeId;

      const citizenshipOptions = await $api.lookups.getCitizenshipStatusOptions(
        applicationId,
        lenderId,
        productTypeId,
        programTypeId,
      );

      if (citizenshipOptions.success) {
        store.dispatch("setCitizenshipTypeOptions", citizenshipOptions.results);
      } else {
        store.dispatch("setCitizenshipTypeOptions", []);
      }
    },
  },
  getters: {
    ValidApplicantAge(state: IApplicantPersonalInfoState) {
      return state.applicantAge && state.applicantAge > 0 && state.applicantAge <= 150;
    },
    SameDOB(state: IApplicantPersonalInfoState) {
      return state.sameDOB;
    },
    SameSSN(state: IApplicantPersonalInfoState) {
      return state.sameSSN;
    },
    ApplicantAge(state: IApplicantPersonalInfoState) {
      return state.applicantAge;
    },
    ApplicantDateOfBirth(state: IApplicantPersonalInfoState) {
      return state.applicantDateOfBirth;
    },
    AgeOfMajority(state: IApplicantPersonalInfoState) {
      let ageOfMajority = 18;
      switch (state.applicantState) {
        case "1":
        case "AL":
        case 1:
          ageOfMajority = 19;
          break;
        case "46":
        case "PR":
        case 46:
          ageOfMajority = 21;
          break;
        default:
          ageOfMajority = 18;
          break;
      }

      return ageOfMajority;
    },
    AgeOfMajorityError(_state: IApplicantPersonalInfoState, getters: any) {
      if (Number(getters.ApplicantAge) === 0) {
        return true;
      }
      return getters.AgeOfMajority > getters.ApplicantAge && getters.ApplicantAge > 0 && getters.ApplicantAge <= 150;
    },
    AgeOfMajorityErrorMessage() {
      const coApplicantTypeDisplayName = store.getters["coApplicantTypeDisplayName"]?.toLowerCase();
      const currentRoute = store.state?.appData?.currentState?.route;

      switch (currentRoute) {
        case "borrowerPersonalInfo":
        case "refiBorrowerPersonalInfo":
          if (store.state.appData?.application?.programTypeId === ProgramIds.PARENT) {
            return `Borrowers must meet the Age of Majority to qualify.`;
          }
          return `Borrowers who do not meet the Age of Majority should add a ${coApplicantTypeDisplayName} to qualify.`;
        case "borrowerInfo":
        case "refiBorrowerInfo":
          return "";
        case "coborrowerInfo":
        case "coborrowerPersonalInfo":
        case "coborrowerInvitedApplicantInfo":
        case "refiCosignerInfo":
        case "refiCosignerPersonalInfo":
        case "refiCosignerInvitedApplicantInfo":
          return `The ${coApplicantTypeDisplayName} must meet the Age of Majority to qualify.`;
        default:
          return "";
      }
    },
    CitizenshipStatusHelpText() {
      const citizenshipTypeOptions = store.getters["getCitizenshipTypeOptions"];
      const coApplicantDisplayName = store.getters["coApplicantTypeDisplayName"]?.toLowerCase();

      const nonUSCitizenWithSSN = citizenshipTypeOptions.find(
        (c: Option) => c.value === CitizenshipStatusTypeIds.NonUSCitizenWithSSN,
      );

      if (nonUSCitizenWithSSN) {
        return `Borrower and ${coApplicantDisplayName} can be United States citizen, permanent resident, or non-citizen with a Social Security Number or Individual Tax Identification Number (ITIN). All applicants must have a valid Social Security Number or ITIN. Non-citizen residents will be required to submit proof of residency (i.e. Visa) that includes expiration date.`;
      } else {
        return `A US Citizen has acquired citizenship by either being born in the United States or through naturalization. A US Permanent resident has been issued a "green card" and has been granted permission to live and work in the US indefinitely.`;
      }
    },
  },
};

export default applicantPersonalInformationModule;
